import { useEffect, useState } from 'react';
import { Fetch } from '../helpers/fetchWrapper';
import { useCancellablePromise } from '../helpers/promiseHandler';

const STATUS = {
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};

const uniboxService = {
  _url: `${process.env.REACT_APP_CAMPAIGN_API_URL}/unibox`,

  GetAllUserEmails(searchQuery, fetch) {
    const [user_emails, setUserEmails] = useState({
      user_emails: [],
      totalItems: 0,
    });
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getAllUserEmails(searchQuery))
        .then(res => {
          setUserEmails(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [JSON.stringify(searchQuery), fetch]);

    return {
      user_emails_loading: status === STATUS.LOADING,
      user_emails_error: status === STATUS.ERROR,
      user_emails_data: user_emails,
    };
  },

  GetAllEmailThreads(searchQuery, fetch) {
    const [threads, setThreads] = useState({
      threads: [],
      totalItems: 0,
    });
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getAllThreads(searchQuery))
        .then(res => {
          setThreads(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [JSON.stringify(searchQuery), fetch]);

    return {
      threads_loading: status === STATUS.LOADING,
      threads_error: status === STATUS.ERROR,
      threads_data: threads,
    };
  },

  GetThreadMessages(searchQuery, fetch) {
    const [thread_messages, setThreadMessages] = useState({
      thread_messages: [],
      totalItems: 0,
    });

    const { cancellablePromise } = useCancellablePromise();
    const queryKey = JSON.stringify(searchQuery);
    const [trackedKey, setTrackedKey] = useState(queryKey);
    const [status, setStatus] = useState(searchQuery?.threadId ? STATUS.LOADING : STATUS.SUCCESS);

    // Derive loading state from the current query during render so consumers
    // see LOADING in the same commit that the query changes — avoiding the
    // one-render gap where the effect below hasn't fired yet.
    if (trackedKey !== queryKey) {
      setTrackedKey(queryKey);
      setStatus(searchQuery?.threadId ? STATUS.LOADING : STATUS.SUCCESS);
    }

    useEffect(() => {
      if (!searchQuery?.threadId) {
        setThreadMessages({ thread_messages: [], totalItems: 0 });
        setStatus(STATUS.SUCCESS);
        return;
      }
      cancellablePromise(this.getThreadMessages(searchQuery))
        .then(res => {
          setThreadMessages(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [queryKey, fetch]);

    return {
      thread_messages_loading: status === STATUS.LOADING,
      thread_messages_error: status === STATUS.ERROR,
      thread_messages_data: thread_messages,
    };
  },

  async getAllUserEmails({ page = 1, itemsPerPage = 10, getAll = true }) {
    let res = await Fetch.get(
      `${this._url}/get-all-user-emails?page=${page}&itemsPerPage=${itemsPerPage}&getAll=${getAll}`,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async getUniboxFilterCampaigns({ campaignTypes = '', page = 1, itemsPerPage = 10, searchText = '', getAll = false }) {
    const q = new URLSearchParams();
    q.set('page', String(page ?? 1));
    q.set('itemsPerPage', String(itemsPerPage ?? 10));
    q.set('getAll', String(getAll === true));
    if (campaignTypes) q.set('campaignTypes', String(campaignTypes));
    if (searchText) q.set('searchText', String(searchText));

    let res = await Fetch.get(`${this._url}/get-unibox-filter-campaigns?${q.toString()}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async getAllThreads({
    filterType,
    page = 1,
    itemsPerPage = 11,
    messagesPerThread = 20,
    searchText,
    getAll,
    stageStatus,
    campaignId,
    campaignIds,
    linkedinProfileId,
    linkedinProfileIds,
    emailAccountId,
    emailAccountIds,
    campaignTypes,
    responseType,
  }) {
    const q = new URLSearchParams();
    q.set('filterType', filterType ?? 'all');
    q.set('page', String(page ?? 1));
    q.set('itemsPerPage', String(itemsPerPage ?? 11));
    q.set('getAll', String(getAll === true));
    q.set('messagesPerThread', String(messagesPerThread ?? 20));
    if (searchText) q.set('searchText', String(searchText));
    if (stageStatus !== undefined && stageStatus !== null && String(stageStatus).length > 0) {
      q.set('stageStatus', String(stageStatus));
    }
    if (campaignIds) q.set('campaignIds', String(campaignIds));
    else if (campaignId) q.set('campaignId', String(campaignId));
    if (linkedinProfileIds) q.set('linkedinProfileIds', String(linkedinProfileIds));
    else if (linkedinProfileId) q.set('linkedinProfileId', String(linkedinProfileId));
    if (emailAccountIds) q.set('emailAccountIds', String(emailAccountIds));
    else if (emailAccountId) q.set('emailAccountId', String(emailAccountId));
    if (campaignTypes) q.set('campaignTypes', String(campaignTypes));
    if (responseType) q.set('responseType', String(responseType));

    let res = await Fetch.get(`${this._url}/get-email-threads?${q.toString()}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },
  async getThreadMessages({ page = 1, itemsPerPage = 20, getAll, threadId }) {
    const emptyThreadMessages = () => ({
      success: true,
      data: {
        currentPage: page,
        hasNextPage: false,
        hasPreviousPage: page > 1,
        nextPage: page + 1,
        previousPage: Math.max(1, page - 1),
        lastPage: 1,
        totalItems: 0,
        items: [],
      },
    });

    if (
      threadId === undefined ||
      threadId === null ||
      threadId === '' ||
      String(threadId).trim().toLowerCase() === 'null' ||
      String(threadId).trim().toLowerCase() === 'undefined'
    ) {
      return emptyThreadMessages();
    }

    const threadIdNum = Number.parseInt(String(threadId).trim(), 10);
    if (!Number.isInteger(threadIdNum) || threadIdNum < 1) {
      return emptyThreadMessages();
    }

    let res = await Fetch.get(
      `${this._url}/get-thread-messages?threadId=${threadIdNum}&page=${page}&itemsPerPage=${itemsPerPage}&getAll=${getAll}`,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },
  async replyMessage(payload) {
    let res = await Fetch.post(`${this._url}/reply-message`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async sendMessage(payload) {
    let res = await Fetch.post(`${this._url}/send-message`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async pinnedThread(id, payload) {
    let res = await Fetch.put(`${this._url}/pinned-thread/${id}`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },
  async starredThread(id, payload) {
    let res = await Fetch.put(`${this._url}/starred-thread/${id}`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },
  async updateThreadStatus(id, payload) {
    let res = await Fetch.put(`${this._url}/update-thread-stage/${id}`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async generateResponse(payload) {
    let res = await Fetch.post(`${this._url}/generate-response`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async getLeadLinkedinThreads({ leadId }) {
    let res = await Fetch.get(`${this._url}/get-lead-linkedin-threads?leadId=${leadId}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },
};

export default uniboxService;
